import React from 'react';
import classNames from 'classnames';

import useDispatch from 'client/hooks/useDispatch';
import useSrGoingToMarketV2DataTableController from 'client/components/SrGoingToMarketV2DataTable/useSrGoingToMarketV2DataTableController';

import SrAdminNotes, {
  ADMIN_NOTES_VARIATION_JOB_ORDER_NOTES,
} from 'client/components/SrAdminNotes';
import SrCard, { SR_CARD_BULLET_COLOR_BLUE } from 'client/components/SrCard/SrCard';
import SrModalV2, { MODAL_THEME_STACK } from 'client/components/SrModalV2/SrModalV2';
import SrTooltip, {
  SR_TOOLTIP_ICON_QUESTION_MARK,
  SR_TOOLTIP_POSITION_LEFT,
} from 'client/components/SrTooltip';

import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrGoingToMarketJobIcon from 'client/components/SrGoingToMarketV2DataTable/SrGoingToMarketJobIcon';

import './SrGoingToMarketV2DataTable.less';

const SrGoingToMarketV2DataTable = ({ currentUser }) => {
  const dispatch = useDispatch();
  const controller = useSrGoingToMarketV2DataTableController();

  return (
    <SrDataTableConnected {...controller.getDataTableSettingsFromProps()}>
      {({
        listings,
        reloadItems,
        dataTableApiState,
        onInfiniteScrollWrapperRef,
        dataTableIsFullyLoaded,
      }) => {
        // eslint-disable-line max-len
        if (dataTableApiState.inProgress && listings.length === 0) {
          return (
            <div>
              <div className="loading-gif clearfix" />
            </div>
          );
        }

        if (listings.length === 0 && dataTableIsFullyLoaded) {
          return (
            <SrText font="greycliff" fontSize="20px" color="blueGreycliff">
              No listings found.
            </SrText>
          );
        }

        return (
          <>
            <SrBox styleName="SrGoingToMarketV2DataTable">
              <SrBox styleName="headersContainer">
                <SrBox styleName="headers">
                  {controller.headers.map((header) => (
                    <SrBox key={`header_${header.id}`} styleName="headerCell">
                      {header.content}
                    </SrBox>
                  ))}
                </SrBox>
              </SrBox>
              <SrBox styleName="listingsContainer" ref={onInfiniteScrollWrapperRef}>
                {listings.map((listing) => (
                  <SrBox key={`listing_${listing.id}`} styleName="listing">
                    <SrBox styleName="listingCell">
                      <SrBox styleName="listingSubCell">
                        {controller
                          .getPhysicalRequirements(listing)
                          .map(({ id, icon, color, text }) => (
                            <SrBox
                              marginRight="4px"
                              key={`physicalRequirement_${listing.id}_${id}`}
                            >
                              <SrGoingToMarketJobIcon icon={icon} color={color} text={text} />
                            </SrBox>
                          ))}
                      </SrBox>
                    </SrBox>
                    <SrBox styleName="listingCell promiseDate">
                      <SrBox
                        styleName={classNames(
                          'listingSubCell',
                          controller.getPromiseDateColor({ promiseDate: listing.dueDate }),
                        )}
                      >
                        {controller.formatDate(listing.dueDate)}
                        {listing.dueDateUpdates.length > 0 && (
                          <SrBox marginLeft="4px" marginBottom="4px">
                            <SrTooltip
                              position={SR_TOOLTIP_POSITION_LEFT}
                              iconName={SR_TOOLTIP_ICON_QUESTION_MARK}
                              tooltipContentStyle={{ width: '328px' }}
                            >
                              {controller.getDueDateUpdatesText({
                                dueDateUpdates: listing.dueDateUpdates,
                              })}
                            </SrTooltip>
                          </SrBox>
                        )}
                      </SrBox>
                    </SrBox>
                    <SrBox styleName="listingCell dueDate">
                      {(listing.jobOrders || []).map(({ id, dueDate, status }) => (
                        <SrBox
                          key={`dueDate_${listing.id}_${id}`}
                          styleName={classNames(
                            'listingSubCell',
                            'dueDate',
                            controller.getDueDateColor({ dueDate, jobOrderStatus: status }),
                          )}
                        >
                          {controller.formatDate(dueDate)}
                        </SrBox>
                      ))}
                    </SrBox>
                    <SrBox styleName="listingCell address">
                      <SrBox styleName="listingSubCell">{listing.addressStreetDisplay}</SrBox>
                    </SrBox>
                    <SrBox styleName="listingCell market">
                      <SrBox styleName="listingSubCell">{listing.marketDisplayName}</SrBox>
                    </SrBox>
                    <SrBox styleName="listingCell">
                      {(listing.jobOrders || []).map(({ id, status, driverName }) => (
                        <SrBox
                          key={`status_${listing.id}_${id}`}
                          styleName={classNames(
                            'listingSubCell',
                            controller.getJobOrderStatusColor({ jobOrderStatus: status }),
                          )}
                        >
                          {controller.getJobOrderStatus({ status, driverName })}
                        </SrBox>
                      ))}
                    </SrBox>
                    <SrBox styleName="listingCell">
                      {(listing.jobOrders || []).map(({ id, jobs }) => (
                        <SrBox key={`completed_${listing.id}_${id}`} styleName="listingSubCell">
                          {controller
                            .getJobIcons({ jobs })
                            .map(({ id: jobId, icon, color, text }) => (
                              <SrBox key={`jobIcon_${listing.id}_${id}_${jobId}`} marginRight="4px">
                                <SrGoingToMarketJobIcon icon={icon} color={color} text={text} />
                              </SrBox>
                            ))}
                        </SrBox>
                      ))}
                    </SrBox>
                    <SrBox styleName="listingCell notes">
                      {(listing.jobOrders || []).map(({ id, jobOrderNotes }) => (
                        <SrBox
                          cursor="pointer"
                          styleName="listingSubCell"
                          key={`jobOrderNotes_${listing.id}_${id}`}
                          onClick={() =>
                            controller.onNotesClick({
                              jobOrderId: id,
                              listingId: listing.listingId,
                            })
                          }
                        >
                          {controller.getNotesCount({ notesCount: jobOrderNotes.length })}
                        </SrBox>
                      ))}
                    </SrBox>
                  </SrBox>
                ))}
              </SrBox>
            </SrBox>
            {controller.isNotesModalOpen && (
              <SrModalV2
                closeOnBackgroundClick
                showCloseButton={false}
                contentMaxWidth="546px"
                contentPadding="0px"
                theme={MODAL_THEME_STACK}
                onClose={controller.onNotesModalClose}
              >
                <SrCard
                  header="Job Order Notes (Admin-Only)"
                  subheader={`Order # ${controller.selectedJobOrderId || 'N/A'}`}
                  color={SR_CARD_BULLET_COLOR_BLUE}
                  maxWidth="none"
                >
                  <SrAdminNotes
                    jobOrderId={controller.selectedJobOrderId}
                    variation={ADMIN_NOTES_VARIATION_JOB_ORDER_NOTES}
                    dispatch={dispatch}
                    onNoteCreated={() =>
                      reloadItems({
                        count: 1,
                        filter: {
                          'listings.id': controller.selectedListingId,
                        },
                      })
                    }
                    onNoteDeleted={() =>
                      reloadItems({
                        count: 1,
                        filter: {
                          'listings.id': controller.selectedListingId,
                        },
                      })
                    }
                    currentUser={currentUser}
                  />
                </SrCard>
              </SrModalV2>
            )}
          </>
        );
      }}
    </SrDataTableConnected>
  );
};

export default SrGoingToMarketV2DataTable;
